import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import DOMPurify from "dompurify";
import { motion, AnimatePresence } from "framer-motion";
import { apiService } from "../../api/apiService";
import {
    FiUser, FiMail, FiPhone, FiPhoneCall, FiBriefcase, FiLayers,
    FiUserCheck, FiShield, FiCheck, FiAlertTriangle, FiCloud, FiHardDrive, FiInfo, FiHome,
    FiExternalLink, FiChevronLeft, FiChevronRight, FiLock, FiSend,
} from "react-icons/fi";
import { shakeModal } from "../../utils/modalAttention";
import { localizedPath } from "../../hooks/useLocalizedNavigate";
import s from "./ProfileSetupModal.module.css";

// Spłaszcza drzewo komórek wybranego działu — niesie też rozwiązanego dyrektora per węzeł.
const flattenUnits = (nodes, depth = 0, out = []) => {
    for (const n of nodes || []) {
        out.push({ id: n.id, name: n.name, depth, directorId: n.directorId, directorName: n.directorName });
        if (n.units?.length) flattenUnits(n.units, depth + 1, out);
    }
    return out;
};

const phoneDigits = (v) => (v || "").replace(/\D/g, "");
const phoneValid = (v) => { const d = phoneDigits(v); return d.length >= 7 && d.length <= 15; };

/**
 * Nieusuwalny formularz konfiguracji konta (onboarding 1. logowania). Bramka jest po stronie serwera
 * (User.ProfileSetupCompletedAt) — okno pokazuje się aż do wysłania danych. Kontekstowe: konto BEZ
 * działu wypełnia też przypisanie organizacyjne, konto z działem tylko brakujące dane kontaktowe.
 *
 * [M, brief 2026-09-07] „każde okno wygląda inaczej niż aplikacja".
 *
 * CO BYŁO ŹLE: okno miało 20,5rem CIEMNEJ KOLUMNY BOCZNEJ (slate + logo + lista kroków + notka),
 * układu, którego nie ma ŻADEN inny modal w aplikacji — pierwszy ekran nowego pracownika wyglądał
 * jak osobny produkt. Do tego warstwa spoza skali z-index i stopka, która przy dłuższym kroku
 * mogła zostać przycięta (brak `flex: 1` / `min-height: 0` na treści).
 *
 * PO ZMIANIE: kreator wielokrokowy w języku aplikacji (wzorzec `TileWizardModal`, kanon okien
 * z `docs/DESIGN-SYSTEM-PODSTRON.md` rozdz. 11): nagłówek na tokenach → pasek postępu → stepper
 * z krokami → treść (jedyny element ze scrollem) → stopka z przyciskami po prawej. Wszystko, co
 * niósł ciemny panel (kroki, „co dalej"), ZOSTAŁO — tylko w języku aplikacji.
 *
 * LOGIKA KROKÓW, zakres danych i kontrakt z backendem BEZ ZMIAN — pilnuje tego zapadka
 * `__tests__/profileSetupModal.kroki.test.js`; wygląd zamraża `__tests__/profileSetupModal.wyglad.test.js`.
 */
const ProfileSetupModal = ({ status, onCompleted, onZamknijWPodgladzie = null }) => {
    const [prefill, setPrefill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [step, setStep] = useState(0);

    const [businessPhone, setBusinessPhone] = useState("");
    const [hasNoBusinessPhone, setHasNoBusinessPhone] = useState(false);
    const [landlinePhone, setLandlinePhone] = useState("");
    const [hasNoLandlinePhone, setHasNoLandlinePhone] = useState(false);
    const [deptId, setDeptId] = useState("");
    const [unitId, setUnitId] = useState("");
    const [customUnit, setCustomUnit] = useState("");
    const [useCustomUnit, setUseCustomUnit] = useState(false);
    const [rodoAccepted, setRodoAccepted] = useState(false);
    const [rodoExpanded, setRodoExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        apiService.getRegistrationPrefill()
            .then((res) => {
                if (cancelled) return;
                const p = res.data || {};
                setPrefill(p);
                setBusinessPhone(p.businessPhone || "");
                setHasNoBusinessPhone(!!p.hasNoBusinessPhone);
                setLandlinePhone(p.landlinePhone || "");
                setHasNoLandlinePhone(!!p.hasNoLandlinePhone);
                setDeptId(p.chosenDepartmentId || "");
                setUnitId(p.chosenUnitId || "");
                setCustomUnit(p.customUnitText || "");
                setUseCustomUnit(!!p.customUnitText);
            })
            .catch(() => setError("Nie udało się pobrać danych formularza."))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const hasDept = !!prefill?.hasDepartment;

    // Kroki są DANE-ZALEŻNE: konto, które ma już dział, nie dostaje kroku przypisania —
    // pokazywanie mu pustego kroku byłoby myleniem go pracą, której nie ma do wykonania.
    const steps = useMemo(() => ([
        { key: "dane", title: "Dane kontaktowe", desc: "Telefony, pod którymi jesteś osiągalny" },
        ...(hasDept ? [] : [{ key: "przypisanie", title: "Przypisanie organizacyjne", desc: "Dział i komórka, w której pracujesz" }]),
        { key: "zgoda", title: "Zgoda i wysyłka", desc: "Klauzula RODO i potwierdzenie" },
    ]), [hasDept]);

    const currentStep = steps[Math.min(step, steps.length - 1)];

    const selectedDept = useMemo(
        () => (prefill?.departments || []).find((d) => String(d.id) === String(deptId)) || null,
        [prefill, deptId]
    );
    const units = useMemo(() => (selectedDept ? flattenUnits(selectedDept.units) : []), [selectedDept]);
    const selectedUnit = useMemo(() => units.find((u) => String(u.id) === String(unitId)) || null, [units, unitId]);

    // Dyrektor odpowiedzialny za wybrany dział/komórkę (auto, read-only). Null = trafi do administratora.
    const approver = useMemo(() => {
        if (useCustomUnit) return { id: null, name: null };
        const id = selectedUnit?.directorId ?? selectedDept?.directorId ?? null;
        const name = selectedUnit?.directorName ?? selectedDept?.directorName ?? null;
        return { id, name };
    }, [useCustomUnit, selectedUnit, selectedDept]);

    /** Walidacja JEDNEGO kroku — ten sam komplet reguł co przy wysyłce, tylko rozbity. */
    const stepError = useCallback((key) => {
        if (key === "dane") {
            if (!hasNoBusinessPhone && !phoneValid(businessPhone))
                return "Telefon służbowy: podaj poprawny numer (7–15 cyfr) lub zaznacz „nie posiadam”.";
            if (!hasNoLandlinePhone && !phoneValid(landlinePhone))
                return "Telefon stacjonarny: podaj poprawny numer (7–15 cyfr) lub zaznacz „nie posiadam”.";
        }
        if (key === "przypisanie") {
            if (!useCustomUnit && !deptId) return "Wybierz dział lub zaznacz „nie znalazłem” i opisz komórkę dla HR.";
            if (useCustomUnit && !customUnit.trim()) return "Opisz swoją komórkę dla HR.";
        }
        if (key === "zgoda" && !rodoAccepted) return "Zaznacz akceptację klauzuli informacyjnej RODO.";
        return "";
    }, [hasNoBusinessPhone, businessPhone, hasNoLandlinePhone, landlinePhone, useCustomUnit, deptId, customUnit, rodoAccepted]);

    const goNext = () => {
        const err = stepError(currentStep.key);
        if (err) { setError(err); return; }
        setError("");
        setStep((n) => Math.min(n + 1, steps.length - 1));
    };
    const goBack = () => { setError(""); setStep((n) => Math.max(n - 1, 0)); };

    const handleSubmit = async () => {
        // Walidujemy KOMPLET — nie tylko ostatni krok — żeby cofnięcie się i zmiana pola
        // nie przepuściły niepoprawnych danych.
        for (const st of steps) {
            const err = stepError(st.key);
            if (err) { setError(err); setStep(steps.indexOf(st)); return; }
        }
        setError("");
        setSubmitting(true);
        try {
            await apiService.submitRegistration({
                businessPhone: hasNoBusinessPhone ? null : businessPhone.trim(),
                hasNoBusinessPhone,
                landlinePhone: hasNoLandlinePhone ? null : landlinePhone.trim(),
                hasNoLandlinePhone,
                chosenDepartmentId: hasDept ? null : (useCustomUnit ? null : (deptId || null)),
                chosenUnitId: hasDept ? null : (useCustomUnit ? null : (unitId || null)),
                customUnitText: hasDept ? null : (useCustomUnit ? customUnit.trim() : null),
                rodoAccepted,
            });
            onCompleted();
        } catch (e) {
            setError(e?.response?.data?.error || "Nie udało się wysłać formularza.");
        } finally {
            setSubmitting(false);
        }
    };

    const sourceBadge = (src) => src === "o365"
        ? <span className={`${s.srcBadge} ${s.srcO365}`}><FiCloud size={10} /> Office365</span>
        : <span className={`${s.srcBadge} ${s.srcApp}`}><FiHardDrive size={10} /> aplikacja</span>;

    const fullName = `${prefill?.firstName || ""} ${prefill?.lastName || ""}`.trim();
    const reviewNote = status?.adminComment || status?.directorComment;
    const isLast = step === steps.length - 1;
    const progress = Math.round(((step + 1) / steps.length) * 100);

    return createPortal(
        <AnimatePresence>
            {/* Bramka: BRAK X i onClose — okna nie da się zamknąć, dane są wymagane. Klik w tło
                nie zamyka, ale MUSI odpowiedzieć (shakeModal), żeby użytkownik nie klikał w pustkę.

                [zgłoszenie właściciela 2026-09-19] JEDEN WYJĄTEK: tryb podglądu cudzego konta.
                Administrator wszedł obejrzeć konto osoby, która nigdy się nie logowała — i to okno
                zablokowało mu CAŁY ekran, bo z definicji nie ma jak go zamknąć. Cytat: „potrzebuję móc
                te komunikaty móc zamknąć — ale to nie zwalnia z obowiązku odczytania i uzupełnienia go
                przez pracownika, którego dotyczą".
                Dlatego zamknięcie jest WYŁĄCZNIE w podglądzie, WYŁĄCZNIE lokalne (nic nie leci na serwer,
                nic nie jest odhaczane) i mówi wprost, że pracownika to nie zwalnia. Dla samego pracownika
                okno zachowuje się dokładnie jak dotąd — bez krzyżyka. */}
            <motion.div
                className={s.overlay}
                onClick={shakeModal}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <motion.div
                    className={s.modal}
                    initial={{ opacity: 0, scale: 0.97, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Konfiguracja konta"
                >
                    {/* ── Nagłówek okna (tokeny, bez gradientu — kanon okien) ── */}
                    <header className={s.header}>
                        {onZamknijWPodgladzie && (
                            <button
                                type="button"
                                className={s.zamknijPodglad}
                                onClick={onZamknijWPodgladzie}
                                data-testid="zamknij-okno-w-podgladzie"
                                title="Zamknij na czas oglądania. Pracownika to NIE zwalnia — przy jego logowaniu okno pojawi się tak samo."
                                aria-label="Zamknij to okno na czas oglądania konta"
                            >
                                ✕
                            </button>
                        )}
                        <div className={s.headText}>
                            <span className={s.eyebrow}>Krok {step + 1} z {steps.length}</span>
                            <h2 className={s.title}>
                                {hasDept ? "Uzupełnij dane konta" : (prefill?.introText || "Skonfiguruj swoje konto")}
                            </h2>
                            <p className={s.subtitle}>
                                {hasDept
                                    ? "Brakuje kilku informacji kontaktowych. Uzupełnij je, aby korzystać z aplikacji bez ograniczeń."
                                    : "Na podstawie tych danych zostaniesz przypisany do działu i uzyskasz pełny dostęp do aplikacji."}
                            </p>
                        </div>
                        <span className={s.badge}><span className={s.badgeDot} /> Pierwsze logowanie</span>
                    </header>

                    <div className={s.progressTrack} aria-hidden="true">
                        <div className={s.progressFill} style={{ width: `${progress}%` }} />
                    </div>

                    {/* Stan procesu widoczny od pierwszej sekundy — tyle, ile było w ciemnym panelu. */}
                    <ol className={s.stepper}>
                        {steps.map((st, i) => (
                            <li
                                key={st.key}
                                className={`${s.step} ${i === step ? s.stepActive : ""} ${i < step ? s.stepDone : ""}`}
                                aria-current={i === step ? "step" : undefined}
                            >
                                <span className={s.stepNum}>{i < step ? <FiCheck size={11} /> : i + 1}</span>
                                <span className={s.stepText}>
                                    <strong>{st.title}</strong>
                                    <em>{st.desc}</em>
                                </span>
                            </li>
                        ))}
                    </ol>

                    {loading ? (
                        <div className={s.loading}><span className={s.spinner} /> Ładowanie danych…</div>
                    ) : (
                        <div className={s.body}>
                            <div className={s.sectionHead}>
                                <span className={s.sectionTitle}>{currentStep?.title}</span>
                                <p className={s.sectionDesc}>{currentStep?.desc}</p>
                            </div>

                            {(status?.requestStatus === "reset" || status?.requestStatus === "correction") && reviewNote && (
                                <div className={s.reviewBanner}>
                                    <FiAlertTriangle size={16} />
                                    <div><strong>Wniosek wymaga poprawy</strong><p>{reviewNote}</p></div>
                                </div>
                            )}

                            {currentStep?.key === "dane" && (
                                <>
                                    <div className={s.identityCard}>
                                        <div className={s.identityAvatar}>{(fullName || "?").charAt(0).toUpperCase()}</div>
                                        <div className={s.identityInfo}>
                                            <div className={s.identityName}><FiUser size={12} /> {fullName || "—"}</div>
                                            <div className={s.identityMail}><FiMail size={12} /> {prefill?.email || "—"}</div>
                                        </div>
                                        <span className={s.identityLock} title="Dane z konta firmowego — nie można ich zmienić">
                                            <FiLock size={11} /> z konta
                                        </span>
                                    </div>

                                    {hasDept && prefill?.currentDepartmentName && (
                                        <div className={s.deptInfo}>
                                            <FiHome size={14} />
                                            <span>Twój dział: <strong>{prefill.currentDepartmentName}</strong></span>
                                        </div>
                                    )}

                                    <div className={s.row2}>
                                        <div className={s.field}>
                                            <label className={s.label}>
                                                <FiPhone size={12} /> Telefon służbowy
                                                {prefill?.businessPhone ? sourceBadge(prefill.businessPhoneSource) : null}
                                            </label>
                                            <input
                                                className={`${s.input} ${!hasNoBusinessPhone && businessPhone && !phoneValid(businessPhone) ? s.inputError : ""}`}
                                                value={businessPhone}
                                                onChange={(e) => setBusinessPhone(e.target.value)}
                                                disabled={hasNoBusinessPhone}
                                                inputMode="tel"
                                                placeholder="np. +48 600 100 200"
                                            />
                                            <label className={s.checkRow}>
                                                <input type="checkbox" checked={hasNoBusinessPhone} onChange={(e) => setHasNoBusinessPhone(e.target.checked)} />
                                                Nie posiadam
                                            </label>
                                        </div>
                                        <div className={s.field}>
                                            <label className={s.label}>
                                                <FiPhoneCall size={12} /> Telefon stacjonarny
                                                {prefill?.landlinePhone ? sourceBadge(prefill.landlinePhoneSource) : null}
                                            </label>
                                            <input
                                                className={`${s.input} ${!hasNoLandlinePhone && landlinePhone && !phoneValid(landlinePhone) ? s.inputError : ""}`}
                                                value={landlinePhone}
                                                onChange={(e) => setLandlinePhone(e.target.value)}
                                                disabled={hasNoLandlinePhone}
                                                inputMode="tel"
                                                placeholder="np. 13 43 700 00"
                                            />
                                            <label className={s.checkRow}>
                                                <input type="checkbox" checked={hasNoLandlinePhone} onChange={(e) => setHasNoLandlinePhone(e.target.checked)} />
                                                Nie posiadam
                                            </label>
                                        </div>
                                    </div>
                                </>
                            )}

                            {currentStep?.key === "przypisanie" && (
                                <>
                                    <div className={s.field}>
                                        <label className={s.label}><FiBriefcase size={12} /> Dział</label>
                                        <select
                                            className={s.input}
                                            value={useCustomUnit ? "" : deptId}
                                            disabled={useCustomUnit}
                                            onChange={(e) => { setDeptId(e.target.value); setUnitId(""); }}
                                        >
                                            <option value="">— wybierz dział —</option>
                                            {(prefill?.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                        </select>
                                    </div>

                                    {!useCustomUnit && units.length > 0 && (
                                        <div className={s.field}>
                                            <label className={s.label}><FiLayers size={12} /> Komórka organizacyjna</label>
                                            <select className={s.input} value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                                                <option value="">— cała jednostka (bez komórki) —</option>
                                                {units.map((u) => (
                                                    <option key={u.id} value={u.id}>{" ".repeat(u.depth * 2)}{u.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <label className={s.checkRow}>
                                        <input type="checkbox" checked={useCustomUnit} onChange={(e) => setUseCustomUnit(e.target.checked)} />
                                        Nie znalazłem swojego działu/komórki — przekaż do HR
                                    </label>
                                    {useCustomUnit && (
                                        <textarea
                                            className={s.textarea}
                                            value={customUnit}
                                            onChange={(e) => setCustomUnit(e.target.value)}
                                            placeholder="Opisz swój dział / komórkę organizacyjną — HR przeanalizuje i przypisze."
                                            rows={3}
                                        />
                                    )}

                                    {/* Dyrektor — AUTO z działu, read-only */}
                                    <div className={s.approver}>
                                        <FiUserCheck size={14} />
                                        <div>
                                            <span className={s.approverLabel}>Wniosek zatwierdzi</span>
                                            <span className={s.approverName}>
                                                {useCustomUnit
                                                    ? "Administrator (komórka spoza struktury)"
                                                    : (approver.name || "Administrator (jesteś dyrektorem działu lub brak dyrektora)")}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}

                            {currentStep?.key === "zgoda" && (
                                <>
                                    {/* Podsumowanie — pracownik widzi, co dokładnie wysyła. */}
                                    <div className={s.summary}>
                                        <div className={s.summaryRow}>
                                            <span>Telefon służbowy</span>
                                            <strong>{hasNoBusinessPhone ? "nie posiadam" : (businessPhone || "—")}</strong>
                                        </div>
                                        <div className={s.summaryRow}>
                                            <span>Telefon stacjonarny</span>
                                            <strong>{hasNoLandlinePhone ? "nie posiadam" : (landlinePhone || "—")}</strong>
                                        </div>
                                        {!hasDept && (
                                            <div className={s.summaryRow}>
                                                <span>Przypisanie</span>
                                                <strong>
                                                    {useCustomUnit
                                                        ? "opis dla HR"
                                                        : [selectedDept?.name, selectedUnit?.name].filter(Boolean).join(" · ") || "—"}
                                                </strong>
                                            </div>
                                        )}
                                    </div>

                                    {/* [polecenie właściciela 2026-09-21] JEDNA KLAUZULA, JEDNO ŹRÓDŁO.
                                        Treść pochodzi z dokumentu prawnego „Polityka prywatności (RODO)" —
                                        z tej samej wersji, którą pracownik czyta pod /dokumenty/rodo i którą
                                        administrator publikuje w „Regulaminie platformy". Wcześniej formularz
                                        pokazywał WŁASNĄ kopię z konfiguracji rejestracji, więc akceptowany tu
                                        tekst i polityka firmy potrafiły się rozjechać bez śladu. */}
                                    {prefill?.rodoClauseMissing && (
                                        <div className={s.rodoBrak} data-testid="rodo-brak-dokumentu">
                                            <FiAlertTriangle size={14} />
                                            <span>
                                                <strong>Klauzula informacyjna RODO nie jest jeszcze opublikowana.</strong>{" "}
                                                Treść bierze się z dokumentu prawnego „Polityka prywatności (RODO)”.
                                                Zgłoś to administratorowi — publikuje się ją w panelu administracyjnym,
                                                w zakładce „Regulamin platformy”.
                                            </span>
                                        </div>
                                    )}
                                    {prefill?.rodoClauseHtml && (
                                        <div className={s.rodo}>
                                            <div className={s.rodoTitle}>
                                                <span>
                                                    <FiInfo size={13} /> Informacja o przetwarzaniu danych (RODO)
                                                    {prefill?.rodoClauseVersion ? ` — wersja ${prefill.rodoClauseVersion}` : ""}
                                                </span>
                                                <a href={localizedPath("dokumentRodo")} target="_blank" rel="noopener noreferrer" className={s.rodoFullLink}>
                                                    <FiExternalLink size={11} /> Pełna klauzula
                                                </a>
                                            </div>
                                            <div
                                                className={`${s.rodoBody} ${rodoExpanded ? s.rodoBodyExpanded : ""}`}
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(prefill.rodoClauseHtml) }}
                                            />
                                            <button type="button" className={s.rodoToggle} onClick={() => setRodoExpanded((v) => !v)}>
                                                {rodoExpanded ? "Zwiń treść" : "Rozwiń pełną treść"}
                                            </button>
                                        </div>
                                    )}
                                    <label className={`${s.checkRow} ${s.rodoCheck}`}>
                                        <input type="checkbox" checked={rodoAccepted} onChange={(e) => setRodoAccepted(e.target.checked)} />
                                        Zapoznałem się z klauzulą informacyjną RODO
                                    </label>
                                </>
                            )}

                            {/* „Co dalej" — treść, którą wcześniej niósł ciemny panel boczny. */}
                            <div className={s.note}>
                                <FiInfo size={13} />
                                <span>
                                    {hasDept
                                        ? "Dane zapisują się od razu — po zapisaniu wracasz do aplikacji."
                                        : "Wniosek trafia do zatwierdzenia. Do czasu decyzji możesz korzystać z aplikacji w podstawowym zakresie."}
                                </span>
                            </div>

                            {error && <div className={s.error}><FiAlertTriangle size={14} /> {error}</div>}
                        </div>
                    )}

                    <footer className={s.footer}>
                        <div className={s.footerInfo}><FiShield size={13} /> Formularz wymagany do uzyskania dostępu</div>
                        <div className={s.footerActions}>
                            {step > 0 && (
                                <button type="button" className={s.backBtn} onClick={goBack} disabled={submitting}>
                                    <FiChevronLeft size={15} /> Wstecz
                                </button>
                            )}
                            {isLast ? (
                                <button type="button" className={s.submitBtn} onClick={handleSubmit} disabled={loading || submitting}>
                                    {submitting ? <span className={s.spinner} /> : (hasDept ? <FiCheck /> : <FiSend />)}
                                    <span>{hasDept ? "Zapisz dane" : "Wyślij wniosek"}</span>
                                </button>
                            ) : (
                                <button type="button" className={s.submitBtn} onClick={goNext} disabled={loading}>
                                    <span>Dalej</span> <FiChevronRight size={15} />
                                </button>
                            )}
                        </div>
                    </footer>
                </motion.div>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
};

export default ProfileSetupModal;
